"use client";

import {
  AnimatePresence,
  motion,
  useInView,
  useMotionValueEvent,
  useScroll,
} from "motion/react";
import { useTranslations } from "next-intl";
import { type ComponentType, useRef, useState } from "react";

import { EASE_OUT_EXPO, Reveal } from "@/components/motion/reveal";
import { TilePattern } from "@/components/motion/tile-pattern";
import { cn } from "@/lib/utils";

import { BoardVisual } from "./visuals/board-visual";
import { ChatVisual } from "./visuals/chat-visual";
import { DeployVisual } from "./visuals/deploy-visual";
import { MeetingVisual } from "./visuals/meeting-visual";
import { MockupVisual } from "./visuals/mockup-visual";
import { NotesVisual } from "./visuals/notes-visual";

// =============================================================================

const STEPS: { key: StepKey; Visual: ComponentType<{ active: boolean }> }[] = [
  { key: "discovery", Visual: ChatVisual },
  { key: "notes", Visual: NotesVisual },
  { key: "documents", Visual: BoardVisual },
  { key: "mockups", Visual: MockupVisual },
  { key: "meeting", Visual: MeetingVisual },
  { key: "development", Visual: DeployVisual },
];

type StepKey =
  | "discovery"
  | "notes"
  | "documents"
  | "mockups"
  | "meeting"
  | "development";

// =============================================================================

function StepNumber({ value }: { value: number }) {
  return (
    <span className="relative flex shrink-0 items-end self-start leading-none font-extrabold">
      <span className="relative inline-block overflow-hidden text-[clamp(3.5rem,5vw,4.75rem)] tracking-[-0.04em]">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={value}
            className="inline-block"
            initial={{ y: "100%" }}
            animate={{ y: "0%" }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
          >
            {value}
          </motion.span>
        </AnimatePresence>
      </span>
      <span className="mb-[0.45rem] ml-1 inline-block size-[0.9rem] bg-brand" />
    </span>
  );
}

function StepChips({ tags }: { tags: string[] }) {
  return (
    <div className="mt-6 flex flex-wrap gap-3">
      {tags.map((tag) => (
        <span
          key={tag}
          className="rounded-full border border-foreground/15 px-5 py-2 text-[0.95rem] text-foreground/80"
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

// =============================================================================

/**
 * "How I work": the steps animate one by one while scrolling (sticky on
 * desktop), each with a live recreation of the matching tool on the right.
 */
export function ProcessSection() {
  const t = useTranslations("Home.process");
  const trackRef = useRef<HTMLDivElement>(null);
  const isTrackInView = useInView(trackRef, { amount: 0.15 });
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });
  const [active, setActive] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (progress) => {
    const next = Math.min(
      STEPS.length - 1,
      Math.max(0, Math.floor(progress * STEPS.length))
    );
    setActive(next);
  });

  const step = STEPS[active];

  return (
    <section className="relative isolate">
      {/* Background: red glow on the left and blinking tiles */}
      <div
        aria-hidden
        className="absolute top-[4%] -left-[20%] -z-10 aspect-square w-[70vw] rounded-full bg-brand/[0.07] blur-[8.75rem]"
      />
      <TilePattern className="absolute inset-0 -z-10" />

      {/* Small screens: the title scrolls with the stacked steps */}
      <div className="container-page pt-[clamp(4rem,8vw,7rem)] lg:hidden">
        <Reveal y={40}>
          <h2 className="text-right text-title">{t("title")}</h2>
        </Reveal>
      </div>

      {/* Desktop: sticky steps */}
      <div
        ref={trackRef}
        className="relative hidden lg:block"
        style={{ height: `${STEPS.length * 85}svh` }}
      >
        <div className="sticky top-0 flex h-svh flex-col">
          {/* The title stays visible while the steps are pinned */}
          <div className="container-page shrink-0 pt-[clamp(5.5rem,12svh,9rem)]">
            <Reveal y={40}>
              <h2 className="text-right text-title">{t("title")}</h2>
            </Reveal>
          </div>
          <div className="flex min-h-0 flex-1 items-center pb-[4svh]">
            <div className="container-page grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] items-center gap-[clamp(2.5rem,5vw,5rem)]">
              <div className="flex gap-6">
                <StepNumber value={active + 1} />
                <div className="min-w-0 flex-1 pt-2">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={step.key}
                      initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, y: -16, filter: "blur(6px)" }}
                      transition={{ duration: 0.45, ease: EASE_OUT_EXPO }}
                    >
                      <p className="text-[clamp(1.25rem,1.7vw,1.6rem)] leading-snug">
                        {t(`steps.${step.key}.text`)}
                      </p>
                      <StepChips
                        tags={t.raw(`steps.${step.key}.tags`) as string[]}
                      />
                    </motion.div>
                  </AnimatePresence>

                  {/* Progress */}
                  <div
                    className="mt-12 flex items-center gap-4"
                    aria-label={t("stepLabel", {
                      current: active + 1,
                      total: STEPS.length,
                    })}
                  >
                    <span className="font-mono text-xs text-muted-foreground tabular-nums">
                      0{active + 1} / 0{STEPS.length}
                    </span>
                    <div className="flex flex-1 gap-1.5">
                      {STEPS.map((item, index) => (
                        <span
                          key={item.key}
                          className="relative h-[0.1875rem] flex-1 overflow-hidden rounded-full bg-foreground/10"
                        >
                          <motion.span
                            className="absolute inset-0 origin-left rounded-full bg-foreground"
                            initial={false}
                            animate={{ scaleX: index <= active ? 1 : 0 }}
                            transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                {STEPS.map(({ key, Visual }, index) => {
                  const isActive = index === active;
                  return (
                    <motion.div
                      key={key}
                      className={cn(
                        index === 0 ? "relative" : "absolute inset-0"
                      )}
                      initial={false}
                      animate={{
                        opacity: isActive ? 1 : 0,
                        scale: isActive ? 1 : 0.94,
                        y: isActive ? 0 : index < active ? -30 : 30,
                        filter: isActive ? "blur(0px)" : "blur(12px)",
                      }}
                      transition={{ duration: 0.7, ease: EASE_OUT_EXPO }}
                      aria-hidden={!isActive}
                      style={{ pointerEvents: isActive ? "auto" : "none" }}
                    >
                      <Visual active={isActive && isTrackInView} />
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Small screens: stacked steps */}
      <div className="container-page flex flex-col gap-24 py-16 lg:hidden">
        {STEPS.map(({ key, Visual }, index) => (
          <MobileStep key={key} index={index} stepKey={key} Visual={Visual} />
        ))}
      </div>
    </section>
  );
}

// =============================================================================

type MobileStepProps = {
  index: number;
  stepKey: StepKey;
  Visual: ComponentType<{ active: boolean }>;
};

function MobileStep({ index, stepKey, Visual }: MobileStepProps) {
  const t = useTranslations("Home.process");
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { amount: 0.4 });

  return (
    <div ref={ref}>
      <Reveal className="flex gap-4">
        <span className="flex items-end text-5xl leading-none font-extrabold">
          {index + 1}
          <span className="mb-1 ml-1 inline-block size-3 bg-brand" />
        </span>
        <div>
          <p className="text-lg leading-snug">{t(`steps.${stepKey}.text`)}</p>
          <StepChips tags={t.raw(`steps.${stepKey}.tags`) as string[]} />
        </div>
      </Reveal>
      <Reveal delay={0.1} className="mt-10">
        <Visual active={isInView} />
      </Reveal>
    </div>
  );
}

// =============================================================================
